import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { useCitas } from '../hooks/useCitas';
import { useAvailability } from '../hooks/useAvailability';
import { useRecurrencia } from '../hooks/useRecurrencia';
import { useFormCita } from '../hooks/useFormCita';

import ModalCrearCita from '../components/Citas/ModalCrearCita';
import ModalReagendar from '../components/Citas/ModalReagendar';
import ModalConfirmarEstado from '../components/Citas/ModalConfirmarEstado';
import ModalConfirmarRecurrente from '../components/Citas/ModalConfirmarRecurrente';
import ModalComprobante from '../components/Citas/ModalComprobante';

import TablasCitas from '../components/Citas/TablasCitas';
import TabListaEspera from '../components/Citas/TabListaEspera';
import TabAnticiposPendientes from '../components/Citas/TabAnticiposPendientes';

const Citas = () => {
  const { user } = useAuth();
  // AuthContext no expone `tenant`: el tenant viene dentro de user.tenant
  const tenantId = user?.tenant?.id;
  const {
    citas,
    listaEspera,
    anticiposPendientes: anticipos,
    clientes,
    servicios,
    fetchCitas: cargarCitas,
    crearCita,
    actualizarEstadoCita: cambiarEstadoCita,
    reagendarCita,
    cancelarSoloEsta,
    cancelarTodasRecurrentes,
    actualizarEsperaEstado,
    eliminarDelEspera,
    confirmarPago: confirmarAnticipo,
    rechazarPago: rechazarAnticipo,
    loading
  } = useCitas(tenantId);

  const { formData, setFormData, validar: validarForm } = useFormCita();
  const { horasDisponibles, horasCargadas, cargarHoras } = useAvailability(tenantId);
  const { esRecurrente, recurrencia, setEsRecurrente, setRecurrencia, generarFechasRecurrentes } = useRecurrencia();

  const [showModalCrear, setShowModalCrear] = useState(false);
  const [showModalReagendar, setShowModalReagendar] = useState(false);
  const [showModalConfirmarEstado, setShowModalConfirmarEstado] = useState(false);
  const [showModalConfirmarRecurrente, setShowModalConfirmarRecurrente] = useState(false);
  const [showModalComprobante, setShowModalComprobante] = useState(false);
  const [tabActivo, setTabActivo] = useState('citas');

  const [citaSeleccionada, setCitaSeleccionada] = useState(null);
  const [nuevoEstadoSeleccionado, setNuevoEstadoSeleccionado] = useState('');
  const [comprobanteSeleccionado, setComprobanteSeleccionado] = useState(null);

  useEffect(() => {
    if (tenantId) {
      cargarCitas();
    }
  }, [tenantId]);

  const handleAbrirModalCrear = () => {
    setFormData({ cliente_id: '', servicio_id: '', fecha_cita: '', hora_inicio: '', notas: '' });
    setEsRecurrente(false);
    setRecurrencia({ tipo: 'semanal', fin_tipo: 'sesiones', num_sesiones: 8, fecha_fin: '' });
    setShowModalCrear(true);
  };

  const handleCerrarModalCrear = () => {
    setShowModalCrear(false);
  };

  const handleCrearCita = async () => {
    if (!validarForm(formData, servicios)) return;

    try {
      if (esRecurrente) {
        const fechasRecurrentes = generarFechasRecurrentes(formData.fecha_cita, recurrencia);
        for (const fecha of fechasRecurrentes) {
          await crearCita({
            ...formData,
            fecha_cita: fecha
          });
        }
      } else {
        await crearCita(formData);
      }
      handleCerrarModalCrear();
      cargarCitas();
    } catch (error) {
      console.error('Error crear cita:', error);
    }
  };

  const handleAbrirModalReagendar = (cita) => {
    setCitaSeleccionada(cita);
    cargarHoras(cita.fecha_cita, cita.servicio_id);
    setShowModalReagendar(true);
  };

  const handleCerrarModalReagendar = () => {
    setCitaSeleccionada(null);
    setShowModalReagendar(false);
  };

  const handleReagendarCita = async (novaFecha, novaHora) => {
    if (!citaSeleccionada) return;

    try {
      await reagendarCita(citaSeleccionada.id, { nueva_fecha: novaFecha, nueva_hora: novaHora });
      handleCerrarModalReagendar();
      cargarCitas();
    } catch (error) {
      console.error('Error reagendar cita:', error);
    }
  };

  const handleAbrirModalConfirmarEstado = (cita, estado) => {
    setCitaSeleccionada(cita);
    setNuevoEstadoSeleccionado(estado);
    setShowModalConfirmarEstado(true);
  };

  const handleCerrarModalConfirmarEstado = () => {
    setCitaSeleccionada(null);
    setNuevoEstadoSeleccionado('');
    setShowModalConfirmarEstado(false);
  };

  const handleConfirmarCambioEstado = async () => {
    if (!citaSeleccionada) return;

    try {
      await cambiarEstadoCita(citaSeleccionada.id, nuevoEstadoSeleccionado);
      handleCerrarModalConfirmarEstado();
      cargarCitas();
    } catch (error) {
      console.error('Error cambiar estado:', error);
    }
  };

  const handleCambiarEstadoDesdeTabla = (cita) => {
    setCitaSeleccionada(cita);
    setShowModalConfirmarEstado(true);
  };

  const handleAbrirModalConfirmarRecurrente = (cita) => {
    setCitaSeleccionada(cita);
    setShowModalConfirmarRecurrente(true);
  };

  const handleCerrarModalConfirmarRecurrente = () => {
    setCitaSeleccionada(null);
    setShowModalConfirmarRecurrente(false);
  };

  const handleConfirmarCancelacion = async (opcion) => {
    if (!citaSeleccionada) return;

    try {
      if (opcion === 'todas' && citaSeleccionada.grupo_recurrencia) {
        await cancelarTodasRecurrentes(citaSeleccionada.grupo_recurrencia);
      } else {
        await cancelarSoloEsta(citaSeleccionada.id);
      }
      handleCerrarModalConfirmarRecurrente();
      cargarCitas();
    } catch (error) {
      console.error('Error cancelar cita:', error);
    }
  };

  const handleAbrirModalComprobante = (comprobante) => {
    setComprobanteSeleccionado(comprobante);
    setShowModalComprobante(true);
  };

  const handleCerrarModalComprobante = () => {
    setComprobanteSeleccionado(null);
    setShowModalComprobante(false);
  };

  const handleOfrecerCitaDesdeEspera = async (item) => {
    try {
      await actualizarEsperaEstado(item.id, 'notificado');
      cargarCitas();
    } catch (error) {
      console.error('Error ofrecer cita:', error);
    }
  };

  const handleEliminarDelEspera = async (itemId) => {
    if (window.confirm('¿Eliminar de la lista de espera?')) {
      try {
        await eliminarDelEspera(itemId);
        cargarCitas();
      } catch (error) {
        console.error('Error eliminar de espera:', error);
      }
    }
  };

  const handleConfirmarAnticipo = async (anticipoId) => {
    try {
      await confirmarAnticipo(anticipoId);
      cargarCitas();
    } catch (error) {
      console.error('Error confirmar anticipo:', error);
    }
  };

  const handleRechazarAnticipo = async (anticipoId) => {
    if (window.confirm('¿Rechazar este anticipo?')) {
      try {
        await rechazarAnticipo(anticipoId);
        cargarCitas();
      } catch (error) {
        console.error('Error rechazar anticipo:', error);
      }
    }
  };

  return (
    <Layout>
    <div style={{ padding: '2rem' }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '2rem',
        paddingBottom: '1.5rem',
        borderBottom: '2px solid #e5e7eb'
      }}>
        <div>
          <h1 style={{ margin: '0 0 0.25rem 0', color: '#1f2937', fontSize: '2rem' }}>
            📅 Citas
          </h1>
          <p style={{ margin: 0, color: '#6b7280' }}>
            Gestiona las citas y el calendario de tu negocio
          </p>
        </div>

        <button
          onClick={handleAbrirModalCrear}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontWeight: '700',
            fontSize: '1rem',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
          onMouseOver={(e) => e.target.style.opacity = '0.8'}
          onMouseOut={(e) => e.target.style.opacity = '1'}
          disabled={loading}
        >
          + Nueva Cita
        </button>
      </div>

      <div style={{
        display: 'flex',
        gap: '0.5rem',
        marginBottom: '1.5rem',
        borderBottom: '2px solid #e5e7eb'
      }}>
        <button
          onClick={() => setTabActivo('citas')}
          style={{
            padding: '1rem 1.5rem',
            backgroundColor: tabActivo === 'citas' ? '#3b82f6' : '#f3f4f6',
            color: tabActivo === 'citas' ? 'white' : '#6b7280',
            border: 'none',
            borderRadius: '8px 8px 0 0',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s',
            fontSize: '0.95rem'
          }}
        >
          📋 Citas {citas.length > 0 && `(${citas.length})`}
        </button>

        <button
          onClick={() => setTabActivo('espera')}
          style={{
            padding: '1rem 1.5rem',
            backgroundColor: tabActivo === 'espera' ? '#3b82f6' : '#f3f4f6',
            color: tabActivo === 'espera' ? 'white' : '#6b7280',
            border: 'none',
            borderRadius: '8px 8px 0 0',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s',
            fontSize: '0.95rem'
          }}
        >
          ⏳ Lista de Espera {listaEspera.length > 0 && `(${listaEspera.length})`}
        </button>

        <button
          onClick={() => setTabActivo('anticipos')}
          style={{
            padding: '1rem 1.5rem',
            backgroundColor: tabActivo === 'anticipos' ? '#3b82f6' : '#f3f4f6',
            color: tabActivo === 'anticipos' ? 'white' : '#6b7280',
            border: 'none',
            borderRadius: '8px 8px 0 0',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s',
            fontSize: '0.95rem'
          }}
        >
          💳 Anticipos Pendientes {anticipos.length > 0 && `(${anticipos.length})`}
        </button>
      </div>

      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        border: '1px solid #e5e7eb'
      }}>
        {tabActivo === 'citas' && (
          <TablasCitas
            citas={citas}
            onEditarCita={handleAbrirModalCrear}
            onReagendarCita={handleAbrirModalReagendar}
            onCambiarEstado={handleCambiarEstadoDesdeTabla}
            onVerComprobante={handleAbrirModalComprobante}
            onCancelarCita={handleAbrirModalConfirmarRecurrente}
            loading={loading}
          />
        )}

        {tabActivo === 'espera' && (
          <TabListaEspera
            listaEspera={listaEspera}
            onOfrecerCita={handleOfrecerCitaDesdeEspera}
            onEliminarDeLista={handleEliminarDelEspera}
            loading={loading}
          />
        )}

        {tabActivo === 'anticipos' && (
          <TabAnticiposPendientes
            anticipos={anticipos}
            onConfirmarAnticipo={handleConfirmarAnticipo}
            onRechazarAnticipo={handleRechazarAnticipo}
            onVerComprobante={handleAbrirModalComprobante}
            loading={loading}
          />
        )}
      </div>

      <ModalCrearCita
        show={showModalCrear}
        onClose={handleCerrarModalCrear}
        onSubmit={handleCrearCita}
        clientes={clientes}
        servicios={servicios}
        formData={formData}
        setFormData={setFormData}
        horasDisponibles={horasDisponibles}
        horasCargadas={horasCargadas}
        esRecurrente={esRecurrente}
        setEsRecurrente={setEsRecurrente}
        recurrencia={recurrencia}
        setRecurrencia={setRecurrencia}
        onCargarHoras={cargarHoras}
        loading={loading}
      />

      <ModalReagendar
        show={showModalReagendar}
        onClose={handleCerrarModalReagendar}
        onSubmit={handleReagendarCita}
        cita={citaSeleccionada}
        horasDisponibles={horasDisponibles}
        horasCargadas={horasCargadas}
        onCargarHoras={cargarHoras}
        loading={loading}
      />

      <ModalConfirmarEstado
        show={showModalConfirmarEstado}
        onClose={handleCerrarModalConfirmarEstado}
        onConfirm={handleConfirmarCambioEstado}
        cita={citaSeleccionada}
        nuevoEstado={nuevoEstadoSeleccionado}
        loading={loading}
      />

      <ModalConfirmarRecurrente
        show={showModalConfirmarRecurrente}
        onClose={handleCerrarModalConfirmarRecurrente}
        onConfirm={handleConfirmarCancelacion}
        cita={citaSeleccionada}
        loading={loading}
      />

      <ModalComprobante
        show={showModalComprobante}
        onClose={handleCerrarModalComprobante}
        comprobante={comprobanteSeleccionado}
      />
    </div>
    </Layout>
  );
};

export default Citas;
